/**
 * Feedback modal — "something isn't working" reports sent to the operator.
 * Opened from the account popover and the help widget footer. XSS-safe
 * (built with createElement/textContent, same discipline as promptModal).
 */

import { client } from '../api/client.js';
import { toast } from './toast.js';

const CATEGORIES = [
  { value: 'bug', label: "Something's broken" },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

export function openFeedbackModal({ category = 'bug' } = {}) {
  // Guard against double-open (e.g. rapid double-click on the popover item)
  if (document.querySelector('[data-testid="feedback-modal"]')) return;

  let submitting = false;
  const cleanup = () => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
  };

  const overlay = document.createElement('div');
  overlay.className = 'g-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && !submitting) cleanup();
  });

  const dialog = document.createElement('div');
  dialog.className = 'g-modal feedback-modal';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'feedback-modal-title');
  dialog.dataset.testid = 'feedback-modal';

  const titleEl = document.createElement('h3');
  titleEl.id = 'feedback-modal-title';
  titleEl.className = 'g-modal__title';
  titleEl.textContent = 'Send Feedback';
  dialog.appendChild(titleEl);

  const msgEl = document.createElement('p');
  msgEl.className = 'g-modal__msg';
  msgEl.textContent = 'Spotted a bug or have an idea? It goes straight to the team.';
  dialog.appendChild(msgEl);

  const select = document.createElement('select');
  select.className = 'g-modal__input feedback-modal__select';
  select.dataset.testid = 'feedback-category';
  select.setAttribute('aria-label', 'Feedback type');
  for (const c of CATEGORIES) {
    const opt = document.createElement('option');
    opt.value = c.value;
    opt.textContent = c.label;
    if (c.value === category) opt.selected = true;
    select.appendChild(opt);
  }
  dialog.appendChild(select);

  const textarea = document.createElement('textarea');
  textarea.className = 'g-modal__input feedback-modal__textarea';
  textarea.dataset.testid = 'feedback-message';
  textarea.rows = 5;
  textarea.maxLength = 5000;
  textarea.placeholder = "What happened? What did you expect? The more detail, the faster we can fix it.";
  textarea.setAttribute('aria-label', 'Feedback message');
  dialog.appendChild(textarea);

  const errEl = document.createElement('div');
  errEl.className = 'feedback-modal__error';
  errEl.hidden = true;
  dialog.appendChild(errEl);

  const footer = document.createElement('div');
  footer.className = 'g-modal__footer';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'g-modal__btn g-modal__btn--cancel';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.onclick = () => { if (!submitting) cleanup(); };

  const sendBtn = document.createElement('button');
  sendBtn.type = 'button';
  sendBtn.className = 'g-modal__btn g-modal__btn--confirm';
  sendBtn.dataset.testid = 'feedback-submit';
  sendBtn.textContent = 'Send';
  sendBtn.onclick = async () => {
    const message = textarea.value.trim();
    if (!message) {
      errEl.textContent = 'Please describe the issue before sending.';
      errEl.hidden = false;
      textarea.focus();
      return;
    }
    submitting = true;
    sendBtn.disabled = true;
    cancelBtn.disabled = true;
    sendBtn.textContent = 'Sending…';
    errEl.hidden = true;
    try {
      await client.submitFeedback({
        category: select.value,
        message,
        page_context: window.location.hash || '#home',
      });
      cleanup();
      toast('success', "Thanks — your feedback is in. We'll take a look.");
    } catch (err) {
      submitting = false;
      sendBtn.disabled = false;
      cancelBtn.disabled = false;
      sendBtn.textContent = 'Send';
      errEl.textContent = err?.message || 'Failed to send. Please try again.';
      errEl.hidden = false;
    }
  };

  footer.appendChild(cancelBtn);
  footer.appendChild(sendBtn);
  dialog.appendChild(footer);
  overlay.appendChild(dialog);

  const onKey = (e) => {
    if (e.key === 'Escape' && !submitting) cleanup();
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);

  requestAnimationFrame(() => textarea.focus());
}
